import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { useSiteData, useLocaleText } from "../contexts/SiteDataContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Navbar = () => {
  const { t } = useTranslation();
  const { siteContent, services } = useSiteData();
  const { pick } = useLocaleText();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileServicesOpen(false);
  }, [location.pathname]);

  const allRental = (services || []).filter((s) => s.type === "rental");
  const featuredRental = allRental.slice(0, 9);
  const otherRental = allRental.slice(9);
  const otherServices = (services || []).filter((s) => s.type === "other");

  const phones = siteContent?.phones || ["+974 50871458"];
  const email = siteContent?.email || "info@ragimetrdingcontracting.com";
  const crNumber = siteContent?.cr_number || "220664";
  const logo = siteContent?.logo || "/logo.png";
  const location_text = pick(siteContent?.location) || "Doha, Qatar";

  const navLinks = [
    { to: "/", label: t("nav.home") },
    { to: "/about", label: t("nav.about") },
    { to: "/services", label: t("nav.services"), hasDropdown: true },
    { to: "/gallery", label: t("nav.gallery") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white border-b transition-all ${
        scrolled ? "border-[#111111]" : "border-[#E5E7EB]"
      }`}
      data-testid="site-navbar"
    >
      {/* Top utility bar */}
      <div className="hidden md:block bg-[#111111] text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center gap-4">
          <span className="font-mono-rtc tracking-widest uppercase">
            {t("common.crNumber")} {crNumber} · {location_text}
          </span>
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              {phones.map((p, i) => (
                <a key={i} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-[#FF5A00] transition-colors" data-testid={`topbar-phone-${i}`}>
                  <Phone size={12} /> <span className="text-xs">{p}</span>
                </a>
              ))}
            </div>
            <a href={`mailto:${email}`} className="hover:text-[#FF5A00] transition-colors" data-testid="topbar-email">
              {email}
            </a>
            <LanguageSwitcher variant="minimal" />
          </div>
        </div>
      </div>

      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-[4.5rem] sm:h-[4.75rem] lg:h-[5.25rem] flex items-center justify-between">
        <Link to="/" className="flex flex-col items-start shrink-0 leading-none" data-testid="navbar-logo-link">
          <img src={logo} alt="RTC logo" className="h-[38px] sm:h-[44px] lg:h-[50px] w-auto object-contain" />
          <span className="font-mono-rtc text-[7px] sm:text-[8px] lg:text-[9px] uppercase tracking-[0.06em] text-[#FF5A00] whitespace-nowrap font-bold leading-none mt-1 sm:mt-1.5 lg:mt-2 ltr:pl-1 rtl:pr-1">
            {t("nav.logoSubtitle")}
          </span>
        </Link>

        <ul className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.hasDropdown ? (
              <li key={link.to} className="group relative">
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-4 py-2 font-medium uppercase tracking-wide text-sm transition-colors ${
                      isActive ? "text-[#FF5A00]" : "text-[#111111] hover:text-[#FF5A00]"
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  <ChevronDown size={14} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="absolute top-full ltr:left-0 rtl:right-0 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 pt-2" data-testid="services-dropdown">
                  <div className="bg-white border border-[#111111] shadow-[4px_4px_0_#111111] w-[600px] xl:w-[680px] grid grid-cols-3">
                    {/* Featured Rental */}
                    <div className="p-4 border-r border-[#E5E7EB] bg-[#FAFAFA]">
                      <Link to="/services/rental" className="group/h flex items-center justify-between mb-1 font-display text-sm uppercase font-bold text-[#111111] hover:text-[#FF5A00] transition-colors" data-testid="dropdown-rental-heading">
                        <span>{t("nav.rental")}</span>
                        <span className="text-[#FF5A00] group-hover/h:translate-x-0.5 transition-transform">→</span>
                      </Link>
                      <div className="w-6 h-[2px] bg-[#FF5A00] mb-3" />
                      <ul className="space-y-1">
                        {featuredRental.map((eq) => (
                          <li key={eq.slug}>
                            <Link to={`/services/${eq.slug}`} className="group/i flex items-center gap-1.5 text-[12px] text-[#52525B] hover:text-[#FF5A00] transition-colors py-0.5" data-testid={`dropdown-rental-${eq.slug}`}>
                              <span className="w-1 h-1 bg-[#FF5A00] rounded-full opacity-60 group-hover/i:opacity-100 transition-opacity shrink-0" />
                              <span className="truncate">{pick(eq.name)}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Other Rental Equipment */}
                    <div className="p-4 border-r border-[#E5E7EB]">
                      <Link to="/services/rental" className="group/h flex items-center justify-between mb-1 font-display text-sm uppercase font-bold text-[#111111] hover:text-[#FF5A00] transition-colors" data-testid="dropdown-other-rental-heading">
                        <span>{t("nav.other")}</span>
                        <span className="text-[#FF5A00] group-hover/h:translate-x-0.5 transition-transform">→</span>
                      </Link>
                      <div className="w-6 h-[2px] bg-[#FF5A00] mb-3" />
                      {otherRental.length > 0 ? (
                        <ul className="space-y-1">
                          {otherRental.map((eq) => (
                            <li key={eq.slug}>
                              <Link to={`/services/${eq.slug}`} className="group/i flex items-center gap-1.5 text-[12px] text-[#52525B] hover:text-[#FF5A00] transition-colors py-0.5" data-testid={`dropdown-other-rental-${eq.slug}`}>
                                <span className="w-1 h-1 bg-[#FF5A00] rounded-full opacity-60 group-hover/i:opacity-100 transition-opacity shrink-0" />
                                <span className="truncate">{pick(eq.name)}</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-[11px] text-[#52525B]/70 italic">{t("nav.noOtherEquipment")}</p>
                      )}
                    </div>

                    {/* Contracting Services */}
                    <div className="p-4">
                      <Link to="/services" className="group/h flex items-center justify-between mb-1 font-display text-sm uppercase font-bold text-[#111111] hover:text-[#FF5A00] transition-colors">
                        <span>{t("nav.services")}</span>
                        <span className="text-[#FF5A00] group-hover/h:translate-x-0.5 transition-transform">→</span>
                      </Link>
                      <div className="w-6 h-[2px] bg-[#FF5A00] mb-3" />
                      <ul className="space-y-1">
                        {otherServices.map((s) => (
                          <li key={s.slug}>
                            <Link to={`/services/${s.slug}`} className="group/i flex items-center gap-1.5 text-[12px] text-[#52525B] hover:text-[#FF5A00] transition-colors py-0.5" data-testid={`dropdown-service-${s.slug}`}>
                              <span className="w-1 h-1 bg-[#FF5A00] rounded-full opacity-60 group-hover/i:opacity-100 transition-opacity shrink-0" />
                              <span className="truncate">{pick(s.name)}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </li>
            ) : (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  end={link.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-2 font-medium uppercase tracking-wide text-sm transition-colors ${
                      isActive ? "text-[#FF5A00]" : "text-[#111111] hover:text-[#FF5A00]"
                    }`
                  }
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </NavLink>
              </li>
            ),
          )}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <Link to="/contact" className="inline-flex items-center bg-[#FF5A00] text-white px-6 py-3 font-bold uppercase tracking-wide text-sm hover:bg-[#111111] transition-colors duration-300" data-testid="navbar-cta">
            {t("nav.getQuote")}
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher variant="minimal" />
          <button className="p-2 border-2 border-[#111111]" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t-2 border-[#111111] max-h-[calc(100vh-4.5rem)] overflow-y-auto overscroll-contain" data-testid="mobile-menu">
          <ul className="px-6 py-4 flex flex-col">
            {navLinks.map((link) =>
              link.hasDropdown ? (
                <li key={link.to} className="border-b border-[#E5E7EB]">
                  <button className="w-full flex items-center justify-between py-3 font-medium uppercase tracking-wide" onClick={() => setMobileServicesOpen(!mobileServicesOpen)} data-testid="mobile-services-toggle">
                    {link.label}
                    <ChevronDown size={18} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                  </button>
                  {mobileServicesOpen && (
                    <ul className="pb-4 ltr:pl-4 rtl:pr-4 space-y-2">
                      <li>
                        <Link to="/services" className="block py-1 text-sm text-[#52525B]" data-testid="mobile-nav-services-overview">→ {t("common.viewAll")}</Link>
                      </li>
                      <li>
                        <Link to="/services/rental" className="block py-1 text-sm text-[#FF5A00] font-bold uppercase" data-testid="mobile-nav-rental">{t("nav.rental")}</Link>
                      </li>
                      {featuredRental.length > 0 && (
                        <>
                          <li className="text-[10px] uppercase tracking-widest text-[#52525B] mt-2 font-mono-rtc">{t("nav.featuredEquipment")}</li>
                          {featuredRental.map((eq) => (
                            <li key={eq.slug}>
                              <Link to={`/services/${eq.slug}`} className="block py-1 text-sm" data-testid={`mobile-nav-rental-${eq.slug}`}>· {pick(eq.name)}</Link>
                            </li>
                          ))}
                        </>
                      )}
                      {otherRental.length > 0 && (
                        <>
                          <li className="text-[10px] uppercase tracking-widest text-[#52525B] mt-2 font-mono-rtc">{t("nav.otherEquipment")}</li>
                          {otherRental.map((eq) => (
                            <li key={eq.slug}>
                              <Link to={`/services/${eq.slug}`} className="block py-1 text-sm" data-testid={`mobile-nav-other-rental-${eq.slug}`}>· {pick(eq.name)}</Link>
                            </li>
                          ))}
                        </>
                      )}
                      <li className="text-[10px] uppercase tracking-widest text-[#52525B] mt-2 font-mono-rtc">{t("nav.otherServices")}</li>
                      {otherServices.map((s) => (
                        <li key={s.slug}>
                          <Link to={`/services/${s.slug}`} className="block py-1 text-sm" data-testid={`mobile-nav-${s.slug}`}>· {pick(s.name)}</Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ) : (
                <li key={link.to} className="border-b border-[#E5E7EB]">
                  <NavLink to={link.to} end={link.to === "/"} className={({ isActive }) => `block py-3 font-medium uppercase tracking-wide ${isActive ? "text-[#FF5A00]" : "text-[#111111]"}`} data-testid={`mobile-nav-${link.label.toLowerCase()}`}>
                    {link.label}
                  </NavLink>
                </li>
              ),
            )}
            <li className="pt-4">
              <Link to="/contact" className="block w-full text-center bg-[#FF5A00] text-white px-6 py-3 font-bold uppercase tracking-wide" data-testid="mobile-cta">
                {t("nav.getQuote")}
              </Link>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
